import { useState } from 'react'

// Services
import { useUnAssignPreferenceCard } from 'lib/services/api/reports-service/preference-card/unAssignPreferenceCard'
import {
  useGetProceduresQuery,
  useStartProcedureMutation,
} from 'lib/apollo/hooks'

// Types
import { Surgery } from 'common/types'
import {
  ProcedureListTableProps,
  TShowSpecialRequestModal,
} from './ProcedureList.types'

// Router
import { useNavigate } from 'react-router-dom'

// State
import { useRoomViewStore } from 'store'
import { useDateRange, useNotificationActions } from 'store/notifications'
import { useUser } from 'app/User'

// Utils
import { useIsMobile } from 'lib/utils/useIsMobile'

// Styles
import './ProcedureListTable.scss'

// Utils
import dayjs from 'lib/dayjs'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { loadNotifications } from 'lib/utils/loadNotifications'
import { enqueueSnackbar } from 'notistack'

export const useProcedureListTableLogic = (props: ProcedureListTableProps) => {
  const { events, showDate = false, sortByDate = false, refetch } = props
  const { allowPatientStatusRestrictedActions } = useRoomViewStore()
  const { setNotifications } = useNotificationActions()
  const flags = useFlags()
  const navigate = useNavigate()
  const { isNurse, isRep } = useUser()
  const hasItems = events && events?.length > 0
  const isMobile = useIsMobile()
  const [startProcedure] = useStartProcedureMutation()
  const [isNotificationsOpen, setIsNotificationsOpen] = useState<{
    isOpen: boolean
    surgery?: Surgery
  }>({
    isOpen: false,
  })
  const [isAssignCardModalOpen, setIsAssignCardModalOpen] = useState<{
    isOpen: boolean
    surgery: Surgery | null
  }>({
    isOpen: false,
    surgery: null,
  })

  const [showPatientStatusModal, setShowPatientStatusModal] = useState<{
    show: boolean
    surgery: Surgery | null
  }>({
    show: false,
    surgery: null,
  })

  const hasNotifications = (surgery?: Surgery): boolean => {
    return (
      (surgery?.salesRepsNotified?.[0]?.salesReps?.length as number) > 0 ||
      false
    )
  }

  const dateRange = useDateRange()
  const { refetch: refetchProcedures } = useGetProceduresQuery({})
  const { mutateAsync: unAssignPrefCard } = useUnAssignPreferenceCard()

  const handleStartSurgery = async (surgery: Surgery) => {
    navigate(`/surgeries/${surgery?._id}`)
    if (surgery.status.name !== 'SCANNING') return
    startProcedure({
      variables: {
        procedureId: surgery._id,
      },
    }).then(() => {
      refetchProcedures({
        endDate: dateRange.before,
        startDate: dateRange.after,
        pendingOnly: true,
      })
        .then(({ data }) => {
          const notifications = loadNotifications(data)
          setNotifications(notifications)
        })
        .catch(() => {
          enqueueSnackbar('Failed to start procedure', { variant: 'error' })
        })
    })
  }

  const eventsArray = events && [...events]

  const sortSurgeries = (surgeries: Surgery[]) => {
    if (sortByDate) {
      return surgeries.sort((a: Surgery, b: Surgery) => {
        const aDateTime = a.procedures[0]?.dateTime || ''
        const bDateTime = b.procedures[0]?.dateTime || ''
        if (aDateTime && bDateTime) {
          return dayjs(aDateTime).isBefore(dayjs(bDateTime)) ? -1 : 1
        }
        return 0
      })
    }

    return surgeries.sort((a: Surgery, b: Surgery) => {
      const roomA = a?.visit?.location?.room || ''
      const roomB = b?.visit?.location?.room || ''
      if (roomA < roomB) {
        return -1
      }
      if (roomA > roomB) {
        return 1
      }

      const aDateTime = a.procedures[0]?.dateTime || ''
      const bDateTime = b.procedures[0]?.dateTime || ''
      if (aDateTime && bDateTime) {
        return dayjs(aDateTime).isBefore(dayjs(bDateTime)) ? -1 : 1
      }
      return 0
    })
  }

  const filteredSurgeries = eventsArray?.filter(
    (surgery: Surgery) => surgery.procedures.length > 0
  )

  const sortedSurgeries = sortSurgeries(filteredSurgeries ?? [])

  // feat: special request
  const [showSpecialRequestModal, setShowSpecialRequestModal] =
    useState<TShowSpecialRequestModal>({
      showModal: null,
      surgeryId: null,
      surgeryStatus: null,
    })

  const [loadingSpecialRequests, setLoadingSpecialRequests] = useState(false)

  const handleSpecialRequest = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    surgeryId: string,
    surgeryStatus: Surgery['status']['name']
  ) => {
    e.stopPropagation()
    if (e.currentTarget.id === 'addSpecialRequest') {
      setShowSpecialRequestModal({ showModal: 'add', surgeryId, surgeryStatus })
    } else if (e.currentTarget.id === 'showSpecialRequests') {
      setLoadingSpecialRequests(true)
      setShowSpecialRequestModal({
        showModal: 'show',
        surgeryId,
        surgeryStatus,
      })
    }
  }

  const handleCloseSpecialRequest = () => {
    setShowSpecialRequestModal({
      showModal: null,
      surgeryId: null,
      surgeryStatus: null,
    })
  }

  const [editModalSurgery, setEditModalSurgery] = useState<Surgery | null>(null)
  const [cancelModalSurgery, setCancelModalSurgery] = useState<Surgery | null>(
    null
  )

  const handleCloseCancelModal = () => {
    setCancelModalSurgery(null)
  }

  const handleCloseEditModal = () => {
    setEditModalSurgery(null)
  }

  const handleCloseAssignCardModal = () => {
    setIsAssignCardModalOpen({
      isOpen: false,
      surgery: null,
    })
  }

  const handleCreatePreferenceCard = () => {
    const surgery = isAssignCardModalOpen.surgery
    if (!surgery) return

    handleCloseAssignCardModal()
    navigate(`/preference-cards/new`, {
      state: {
        surgeonId: surgery?.visit?.attendingProvider?._id ?? '',
        procedureType: surgery?.procedures[0]?.description ?? '',
      },
    })
  }

  const handleUnassignPreference = async (surgery: Surgery) => {
    try {
      await unAssignPrefCard({ surgeryId: surgery._id })

      if (refetch) {
        await refetch()
      }

      enqueueSnackbar('Preference card has been unassigned.', {
        variant: 'success',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
    } catch (error) {
      enqueueSnackbar('Failed to unassign preference card.', {
        variant: 'error',
        autoHideDuration: 3000,
        preventDuplicate: true,
      })
      console.error(error)
    }
  }

  return {
    flags,
    isRep,
    isNurse,
    hasItems,
    isMobile,
    showDate,
    sortedSurgeries,
    isNotificationsOpen,
    loadingSpecialRequests,
    showSpecialRequestModal,
    hasNotifications,
    handleStartSurgery,
    handleSpecialRequest,
    setIsNotificationsOpen,
    handleCloseSpecialRequest,
    setLoadingSpecialRequests,
    setShowSpecialRequestModal,
    editModalSurgery,
    setEditModalSurgery,
    handleCloseEditModal,
    setShowPatientStatusModal,
    showPatientStatusModal,
    cancelModalSurgery,
    setCancelModalSurgery,
    handleCloseCancelModal,
    allowPatientStatusRestrictedActions,
    isAssignCardModalOpen,
    setIsAssignCardModalOpen,
    handleCreatePreferenceCard,
    handleUnassignPreference,
  }
}
